/**
 * WebAuthn / passkey ceremonies.
 *
 * Login: beginPasskeyAssertion (anonymous, framework WebAuthnService) →
 * navigator.credentials.get → AuthenticationService.passkeyLogin (the app's
 * Phase-3 endpoint), which verifies the assertion server-side and returns the
 * SAME response shape as the email-code flow (accessToken + 30-day sliding
 * refreshToken + user info) — so callers feed it straight into onLogin.
 *
 * Registration: authenticated begin/finishRegistration against the framework
 * service; the passkey is bound to the logged-in account's email.
 *
 * All binary fields travel as base64url strings (WebAuthn convention, matches
 * the backend's Base64.getUrlDecoder()).
 */

import {
  beginPasskeyAssertion,
  finishPasskeyLogin,
  beginPasskeyRegistration,
  finishPasskeyRegistration,
} from './httpApi';
import { debugLog, debugError } from './debug';
import { diag } from './diag';

/** base64url string -> ArrayBuffer */
function b64urlToBuf(s) {
  const pad = '='.repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

/** ArrayBuffer -> base64url string (no padding) */
function bufToB64url(buf) {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** True when the browser can do WebAuthn at all. */
export function passkeysSupported() {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && !!(navigator.credentials && navigator.credentials.get);
}

const apiOk = (r) => r && (r.header?.apiStatus === 'OK' || r.header?.apiStatus === 'SUCCESS');
const apiError = (r, fallback) =>
  new Error(r?.header?.errors?.[0]?.message || r?.message || fallback);

/**
 * Sign in with a passkey. With an email, the server lists that account's
 * credentials (allowCredentials); without one, the resident-key (discoverable
 * credential) flow lets the platform picker choose — that's the usual mobile
 * Face ID / fingerprint path.
 *
 * @returns the authorise-shaped login response (accessToken, refreshToken,
 *          username, email, userId, accountStatus, ...)
 */
export async function loginWithPasskey(email = null) {
  const begin = await beginPasskeyAssertion(email);
  if (!apiOk(begin) || !begin.challenge) {
    throw apiError(begin, 'Passkey login is not available');
  }

  const publicKey = {
    challenge: b64urlToBuf(begin.challenge),
    timeout: begin.timeout || 60000,
    rpId: begin.rpId,
    userVerification: begin.userVerification || 'preferred',
    allowCredentials: (begin.allowCredentials || []).map((c) => ({
      type: 'public-key',
      id: b64urlToBuf(c.id),
      ...(c.transports && c.transports.length ? { transports: c.transports } : {}),
    })),
  };

  // Breadcrumbs: a failed passkey login can die entirely on the device, with
  // NOTHING reaching the server — the logs then show only beginAssertion and
  // give no clue where it stopped. These record each stage so the ring buffer
  // can be read afterwards (enable with ?diag=1).
  diag('PASSKEY_BEGIN_OK', {
    rpId: begin.rpId,
    allowCredentials: (begin.allowCredentials || []).length,
    userVerification: begin.userVerification || null,
    origin: window.location.origin,
  });

  debugLog('🔑 Passkey assertion starting (rpId:', begin.rpId, ')');
  let cred;
  try {
    cred = await navigator.credentials.get({ publicKey });
  } catch (e) {
    // Record the real name/message before it is generalised upstream.
    diag('PASSKEY_GET_FAILED', { name: e && e.name, message: String((e && e.message) || '').slice(0, 200) });
    throw e;
  }
  if (!cred) {
    diag('PASSKEY_GET_EMPTY');
    throw new Error('No passkey selected');
  }
  diag('PASSKEY_GET_OK', { id: (cred.id || '').slice(0, 12), type: cred.type });

  const payload = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      authenticatorData: bufToB64url(cred.response.authenticatorData),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
      signature: bufToB64url(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64url(cred.response.userHandle) : null,
    },
  };

  diag('PASSKEY_FINISH_SENDING');
  let result;
  try {
    result = await finishPasskeyLogin(payload);
  } catch (e) {
    diag('PASSKEY_FINISH_NETWORK_FAIL', { message: String((e && e.message) || '').slice(0, 200) });
    throw e;
  }

  // The API answers 200 with apiStatus "OK" even for a rejected call and puts
  // the real outcome in header.errors, so a missing accessToken — not the
  // status — is what actually distinguishes failure here.
  if (!apiOk(result) || !result.accessToken) {
    diag('PASSKEY_FINISH_REJECTED', {
      apiStatus: result?.header?.apiStatus || null,
      error: result?.header?.errors?.[0]?.code || result?.header?.errors?.[0]?.message || null,
      hasAccessToken: !!result?.accessToken,
    });
    throw apiError(result, 'Passkey verification failed');
  }
  diag('PASSKEY_FINISH_OK', { hasRefresh: !!result.refreshToken, hasExpiry: !!result.expiryTimeUTC });
  debugLog('🔑 Passkey login OK for', result.email);
  return result;
}

/**
 * Register a new passkey for the logged-in account (requires a valid access
 * token). @param name friendly label shown in the passkey list.
 * @returns the finishRegistration response (credentialIdHex, name, createdAt).
 */
export async function registerPasskey(name, token) {
  const label = (name && name.trim()) || 'Passkey';
  const begin = await beginPasskeyRegistration(token, label);
  if (!apiOk(begin) || !begin.challenge) {
    throw apiError(begin, 'Passkey registration is not available');
  }

  const publicKey = {
    challenge: b64urlToBuf(begin.challenge),
    timeout: begin.timeout || 60000,
    rp: { id: begin.rp?.id, name: begin.rp?.name },
    user: {
      id: b64urlToBuf(begin.user.id),
      name: begin.user.name,
      displayName: begin.user.displayName || begin.user.name,
    },
    pubKeyCredParams: (begin.pubKeyCredParams || []).map((p) => ({
      type: 'public-key',
      alg: p.alg,
    })),
    excludeCredentials: (begin.excludeCredentials || []).map((c) => ({
      type: 'public-key',
      id: b64urlToBuf(c.id),
      ...(c.transports && c.transports.length ? { transports: c.transports } : {}),
    })),
    ...(begin.authenticatorSelection ? {
      authenticatorSelection: {
        ...(begin.authenticatorSelection.authenticatorAttachment
          ? { authenticatorAttachment: begin.authenticatorSelection.authenticatorAttachment } : {}),
        userVerification: begin.authenticatorSelection.userVerification || 'preferred',
        residentKey: begin.authenticatorSelection.residentKey || 'preferred',
        requireResidentKey: !!begin.authenticatorSelection.requireResidentKey,
      },
    } : {}),
    attestation: begin.attestation || 'none',
  };

  debugLog('🔑 Passkey registration starting (rp:', publicKey.rp, ')');
  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('Passkey creation was cancelled');

  const transports = (cred.response.getTransports && cred.response.getTransports()) || [];
  const payload = {
    id: cred.id,
    rawId: bufToB64url(cred.rawId),
    type: cred.type,
    response: {
      attestationObject: bufToB64url(cred.response.attestationObject),
      clientDataJSON: bufToB64url(cred.response.clientDataJSON),
    },
    transports,
    name: label,
  };

  const result = await finishPasskeyRegistration(payload, token);
  if (!apiOk(result)) {
    debugError('Passkey registration failed:', result);
    throw apiError(result, 'Passkey registration failed');
  }
  debugLog('🔑 Passkey registered:', result.credentialIdHex);
  return result;
}
